import { useQuery } from 'react-query'

import { url, getCustomHeaders } from 'components/DataProvider/functions'
import { flow, isNil, reverse, size, sortBy } from 'lodash/fp'
import axios, { type AxiosHeaders } from 'axios'
import { assign } from 'lodash'
import { type Partner } from '@mzc-watchover/adminapp-appsync'
import { useDataProvider } from 'react-admin'
import { type AnnounceItem } from '../../../../cdk/bapp/lib/lambda-api/src/types'

export const useGetSummaryItems = (
  partner: string | undefined
): { isLoading: boolean; data: string[] } => {
  const { data, isLoading } = useQuery({
    queryKey: ['summary', partner],
    queryFn: async () => {
      if (isNil(partner)) return undefined
      if (isNil(url)) return undefined
      const customHeaders = getCustomHeaders({}, partner) as
        | AxiosHeaders
        | undefined
      if (isNil(customHeaders)) return undefined
      const headers = {
        Authorization: customHeaders.get('Authorization')
      }
      if (!isNil(partner)) {
        assign(headers, { 'X-Partner': partner })
      }
      const { status, data } = await axios.get(`${url}/summary`, {
        headers
      })
      if (status !== 200) return undefined
      return data
    }
  })
  return { isLoading, data: !isNil(data) ? data.items : data }
}
export const useGetPartners = (): {
  isLoading: boolean
  data: Array<{ id: string } & Partial<Partner>> | undefined
  total: number
} => {
  const dataProvider = useDataProvider()
  const { data, isLoading } = useQuery({
    queryKey: ['partners'],
    queryFn: async () => {
      const { total } = await dataProvider
        .getList<{ id: string } & Partial<Partner>>('partners', {
          pagination: { page: 1, perPage: 10 },
          filter: {},
          sort: { field: 'id', order: 'ASC' }
        })
        .catch(() => ({ total: undefined }))
      if (isNil(total)) return []
      const { data } = await dataProvider
        .getList<{ id: string } & Partial<Partner>>('partners', {
          pagination: { page: 1, perPage: total },
          filter: {},
          sort: { field: 'name', order: 'ASC' }
        })
        .catch(() => ({ data: undefined }))
      return data
    }
  })
  return { isLoading, data, total: isNil(data) ? 0 : size(data) }
}
export const useGetPartner = (
  id: string | undefined
): {
  isLoading: boolean
  data: ({ id: string } & Partial<Partner>) | undefined
} => {
  const dataProvider = useDataProvider()
  const { data, isLoading } = useQuery({
    queryKey: ['partners', id],
    queryFn: async () => {
      if (isNil(id)) return undefined
      const { data } = await dataProvider
        .getOne<{ id: string } & Partial<Partner>>('partners', {
          id
        })
        .catch(() => ({ data: undefined }))
      return data
    }
  })
  return { isLoading, data }
}
export const useGetAnnounces = (): {
  isLoading: boolean
  data: AnnounceItem[]
} => {
  const dataProvider = useDataProvider()
  const { data, isLoading } = useQuery({
    queryKey: ['announces'],
    queryFn: () => dataProvider['getAnnounces'](),
    staleTime: isNil(process.env['NO_CACHE']) ? 3600 * 1000 : 0
  })
  return {
    isLoading,
    data: flow([sortBy<AnnounceItem>('date'), reverse])(data)
  }
}
export const useGetUserReserve = (
  partner: string,
  id: string
): {
  isLoading: boolean
  data: { data: any; date: string }
  refetch: any
} => {
  const dataProvider = useDataProvider()
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['userReserve', partner, id],
    queryFn: () => dataProvider['getUserReserve'](partner, id),
    staleTime: 0 // isNil(process.env['NO_CACHE']) ? 3600 * 1000 : 0
  })
  return {
    isLoading,
    data,
    refetch
  }
}
export const useGetExistContact = (partner: string | undefined): boolean => {
  const { data } = useQuery({
    queryKey: ['contact', partner],
    queryFn: async () => {
      if (isNil(partner)) return undefined
      if (isNil(url)) return undefined
      const customHeaders = getCustomHeaders({}, partner) as
        | AxiosHeaders
        | undefined
      if (isNil(customHeaders)) return undefined
      const headers = {
        Authorization: customHeaders.get('Authorization')
      }
      if (!isNil(partner)) {
        assign(headers, { 'X-Partner': partner })
      }
      const { status, data } = await axios.get(`${url}/contact/exist`, {
        headers
      })
      if (status !== 200) return undefined
      return data
    }
  })
  return !isNil(data) ? data.data : false
}
